/* eslint-disable no-console */
import type { Context as HonoContext, Next as HonoNext } from 'hono'
import { createRateLimiter as createCore } from '../../core/RateLimiter'
import type { RateShieldContext } from '../../context'
import type {
  RateLimitInfo,
  RateLimitOptions,
  RateLimitStats
} from '../../types'
import { extractIp } from './helpers'

// ── Hono-specific instance type ────────────────────────────────────────────────

/**
 * A rate-limiter instance that works as Hono middleware:
 *
 * ```ts
 * const limiter = createRateLimiter({ maxRequests: 100 })
 * app.use('*', limiter)
 * ```
 */
export interface HonoRateLimiterInstance {
  (c: HonoContext, next: HonoNext): Promise<Response | void>
  destroy(): void | Promise<void>
  reset(key: string): void | Promise<void>
  resetAll(): void | Promise<void>
  getInfo(key: string): Promise<RateLimitInfo | null>
  getStats(): Promise<RateLimitStats>
}

// ── Hono factory ───────────────────────────────────────────────────────────────

/**
 * Create a rate-limiter instance that works as Hono middleware.
 *
 * @example
 * ```ts
 * import { Hono } from 'hono'
 * import { createRateLimiter } from 'rate-shield/hono'
 *
 * const app    = new Hono()
 * const limiter = createRateLimiter({ maxRequests: 100, windowMs: 60_000 })
 *
 * app.use('*', limiter)
 * ```
 */
export const createRateLimiter = (
  options: RateLimitOptions<HonoContext> = {}
): HonoRateLimiterInstance => {
  const core = createCore<HonoContext>({
    keyGenerator: extractIp,
    ...options
  })

  const middleware = async (
    c: HonoContext,
    next: HonoNext
  ): Promise<Response | void> => {
    // Hono middleware can either return a Response (blocked) or call next() (pass).
    let blockedResponse: Response | undefined
    let passed = false

    const ctx: RateShieldContext<HonoContext> = {
      req: c,
      // c.header() stores the header; it will be sent with the Response later.
      setHeader: (name, value) => {
        c.header(name, String(value))
      },
      // Treat as sent once we have created the blocked Response.
      isResponseSent: () => blockedResponse !== undefined,
      block: (_statusCode, body) => {
        // c.json() creates a Response that includes all headers set via c.header().
        blockedResponse = c.json(body as Record<string, unknown>, 429) as Response
      },
      pass: () => {
        passed = true
      }
    }

    await core.handle(ctx)

    if (blockedResponse) return blockedResponse
    if (passed) return next()
  }

  return Object.assign(middleware, {
    destroy: () => core.destroy(),
    reset: (key: string) => core.reset(key),
    resetAll: () => core.resetAll(),
    getInfo: (key: string) => core.getInfo(key),
    getStats: () => core.getStats()
  }) as HonoRateLimiterInstance
}
